export const en = {
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    back: 'Back',
    close: 'Close',
    search: 'Search',
    noData: 'No data',
  },
  tabs: {
    home: 'Home',
    settings: 'Settings',
    profile: 'Profile',
  },
  settings: {
    title: 'Settings',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    version: 'Version',
    about: 'About',
  },
  home: {
    title: 'Home',
    welcome: 'Welcome',
    description: 'Get started by editing the features.',
  },
  admin: {
    nav: {
      dashboard: 'Dashboard',
      samples: 'Samples',
      training: 'Training',
      curriculum: 'Curriculum',
      testers: 'Testers',
      modelQuality: 'Model Quality',
      settings: 'Settings',
      menu: 'Menu',
      collapse: 'Collapse',
    },
    header: { title: 'Admin Panel' },
    reviewIo: {
      importDone: 'Review file imported',
      importError: 'Import failed — check that the file is valid review JSON',
    },
    training: {
      title: 'Training',
      detailTitle: 'Song Detail',
      subtitle:
        'Register practice songs and generate, edit, and save their pitch charts.',
      register: 'Register Song',
      exportList: 'Export List',
      empty: 'No songs registered',
      emptyHint: 'Register a song to start building a pitch chart.',
      loading: 'Loading...',
      notFound: 'Song not found.',
      table: {
        title: 'Title',
        artist: 'Artist',
        key: 'Key',
        bpm: 'BPM',
        duration: 'Duration',
        updated: 'Updated',
      },
      form: {
        title: 'Title',
        titlePlaceholder: 'Song title',
        artist: 'Artist',
        key: 'Key',
        bpm: 'BPM',
        tags: 'Tags',
        tagsPlaceholder: 'Comma separated',
        notes: 'Notes',
        uploadAudio: 'Upload Audio',
        cancel: 'Cancel',
        create: 'Create',
      },
      detail: {
        metaTitle: 'Song Info',
        audioTitle: 'Audio',
        chartTitle: 'Pitch Chart',
        editorHint:
          'Click an empty spot to add a note; drag to move or resize; select then use arrow keys to move (Shift+←/→ to resize) or Delete to remove. Shaded rows are the key’s scale; the grey curve is the pitch the engine extracted.',
        generate: 'Generate from Audio',
        generating: 'Generating...',
        import: 'Import',
        save: 'Save to disk',
        saveDownload: 'Export (download)',
        delete: 'Delete Song',
      },
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Model-QA overview of the validation backlog.',
      greeting: 'Welcome back, {{name}}',
      todayOverview: 'Engine-validation status across samples and sessions.',
      stats: {
        pending: 'Pending validations',
        pendingHint: '{{samples}} samples · {{sessions}} sessions',
        reviewed: 'Reviewed',
        coverage: '{{percent}}% coverage',
        agreement: 'Engine agreement',
        agreementHint: '{{count}} judged',
        disagreements: 'Disagreements',
        disagreementsHint: 'Engine flagged off',
      },
      disagreements: {
        title: 'Recent disagreements',
        empty: 'No engine-vs-human disagreements yet.',
        sample: 'Sample',
        session: 'Session',
      },
      coverage: {
        title: 'Coverage',
        samples: 'Samples reviewed',
        sessions: 'Sessions reviewed',
        techniques: 'Techniques covered',
        testers: 'Testers covered',
      },
    },
    settings: {
      title: 'Settings',
      subtitle: 'Manage reviewer identity and engine version.',
      reviewer: {
        title: 'Reviewer',
        nameLabel: 'Name',
        emailLabel: 'Email',
        roleLabel: 'Role',
        save: 'Save Changes',
      },
      engine: {
        title: 'Engine',
        versionLabel: 'Engine version',
        versionHint: 'Version label stamped on new validations.',
        save: 'Save Changes',
      },
      toast: {
        saved: 'Settings saved',
      },
    },
    samples: {
      title: 'Samples',
      detailTitle: 'Sample Detail',
      subtitle: 'Browse, play, and review voice samples for dataset curation.',
      search: 'Search by file name...',
      count: '{{count}} samples',
      resetFilters: 'Reset filters',
      startReview: 'Start review',
      allReviewed: 'All reviewed',
      verdict: {
        all: 'All',
        unreviewed: 'Unreviewed',
        engine_ok: 'Engine OK',
        engine_off: 'Engine off',
        recapture: 'Recapture',
      },
      metric: {
        pitchAccuracy: 'Pitch accuracy',
        pitchStability: 'Stability',
        sustain: 'Breath',
        vibrato: 'Vibrato',
        resonance: 'Resonance',
        singersFormant: "Singer's formant",
      },
      metricDesc: {
        pitchAccuracy: 'Avg {{cents}} cents off nearest note',
        pitchStability: 'Pitch wobble (std dev) {{cents}} cents',
        sustain: 'Steady phonation for ~{{sec}}s',
        vibrato: 'Regularity of pitch oscillation',
        resonance: 'Resonance from harmonic-to-noise ratio (HNR)',
        singersFormant: '2.8–3.4kHz overtone energy (operatic ring)',
      },
      filter: {
        allExercises: 'All exercises',
        allTechniques: 'All techniques',
      },
      testerLevel: {
        expert: 'Expert',
        amateur: 'Amateur',
      },
      gender: {
        male: 'Male',
        female: 'Female',
      },
      exercise: {
        arpeggios: 'Arpeggios',
        scales: 'Scales',
        long_tones: 'Long tones',
        excerpts: 'Excerpts',
      },
      technique: {
        belt: 'Belt',
        breathy: 'Breathy',
        vibrato: 'Vibrato',
        vocal_fry: 'Vocal fry',
        lip_trill: 'Lip trill',
        straight: 'Straight',
        slow_forte: 'Slow forte',
        slow_piano: 'Slow piano',
        fast_forte: 'Fast forte',
        fast_piano: 'Fast piano',
        messa: 'Messa di voce',
        trill: 'Trill',
        trillo: 'Trillo',
        forte: 'Forte',
        inhaled: 'Inhaled',
        pp: 'Pianissimo',
        spoken: 'Spoken',
      },
      table: {
        file: 'File',
        tester: 'Tester',
        exercise: 'Exercise',
        technique: 'Technique',
        vowel: 'Vowel',
        verdict: 'Verdict',
      },
      vowelNone: '—',
      empty: 'No samples found',
      emptyHint: 'Try adjusting your search or filters.',
      trainingExport: {
        json: 'Training pairs JSON',
        csv: 'Training pairs CSV',
        empty: 'No training pairs to export — reviewed samples required',
        done: 'Exported {{count}} training pairs',
      },
      detail: {
        back: 'Voice Data Review',
        play: 'Play',
        pause: 'Pause',
        spectrogram: 'Spectrogram',
        speed: 'Playback speed',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        noAudio: 'No audio sample available',
        metadata: 'Metadata',
        fileName: 'File name',
        batch: 'Batch',
        reviewPanel: 'Review',
        verdictLabel: 'Verdict',
        tagsLabel: 'Tags',
        tagsPlaceholder: 'Type a tag, press Enter (e.g. strained, resonant)',
        noteLabel: 'Reviewer note',
        notePlaceholder: 'Add a note about this sample...',
        save: 'Save review',
        reviewedBy: 'Reviewed by',
        reviewedAt: 'Reviewed at',
        engineOutput: 'Engine output',
        analyze: 'Analyze',
        analyzeRegion: 'Analyze region',
        reanalyze: 'Re-analyze',
        analyzing: 'Analyzing…',
        region: 'Region',
        clearRegion: 'Clear region',
        selectHint: 'Drag on the waveform to select a region to analyze',
        notAnalyzed: 'Not analyzed yet',
        agree: 'Agree',
        agreeAll: 'Agree all',
        off: 'Off',
        corrected: 'Corrected',
        metricNote: 'Metric note',
        sfVerdict: "Singer's formant verdict",
        sfEngineSays: 'Engine: {{call}}',
        sfMismatch: 'differs from engine',
        present: 'Present',
        absent: 'Absent',
        pitch: 'Pitch',
        centroid: 'Centroid',
        hnr: 'HNR',
        singersFormant: "Singer's formant",
        note: 'Note',
        sfBandHint: "Singer's formant = 2.8–3.4kHz band energy ratio",
        contourTitle: 'Pitch deviation (cents · 0 = in tune)',
        f0: 'f0',
        jitter: 'Jitter',
        shimmer: 'Shimmer',
        sfPeak: 'SF peak',
        sfRel: 'SF rel level',
        spectrumTitle: 'Average spectrum',
        formantsTitle: 'Formant measurements',
        harmonicsTitle: 'Harmonics (dB vs H1)',
        normal: 'Normal',
        high: 'High',
        low: 'Low',
        unmeasured: 'Not measured',
        formant: {
          f1: 'Jaw / openness',
          f2: 'Tongue position',
          f3: 'Resonance / brightness',
          f4: "Singer's formant",
        },
        reviewMetrics: 'Review metrics',
        unsavedWarning: 'You have unsaved changes. Leave this page anyway?',
        reviewProgress: 'Reviewed {{done}} / {{total}}',
        prevSample: 'Previous sample (K)',
        nextSample: 'Next sample (J)',
        kbdHint:
          '1·2·3 verdict · S save+next · J/K move · U next unreviewed · ? help',
        medianDev: 'Median ±{{cents}}¢',
        shortcuts: {
          title: 'Keyboard shortcuts',
          verdict: 'Verdict (ok / off / recapture)',
          save: 'Save, then go to next unreviewed',
          next: 'Next sample',
          prev: 'Previous sample',
          unreviewed: 'Next unreviewed',
          play: 'Play / pause',
          seek: 'Seek 3s back / forward',
          close: 'Close',
        },
      },
      grade: {
        master: 'Master',
        excel: 'Excel',
        good: 'Good',
        clean: 'Clean',
        normal: 'Normal',
        caution: 'Caution',
        crisis: 'Crisis',
      },
      toast: {
        saved: 'Review saved',
      },
    },
    curriculum: {
      title: 'Curriculum review',
      detailTitle: 'Curriculum review',
      intro:
        'Verify each warmup exercise’s pitch, audio, and metadata before the player app is built.',
      loading: 'Loading…',
      loadError: 'Failed to load curriculum',
      notFound: 'Item not found.',
      backToList: 'Back to list',
      programs: 'Programs',
      exercises: 'Exercises',
      stepsN: '{{n}} steps',
      code: 'Code',
      name: 'Name',
      level: 'Level',
      range: 'Range',
      status: 'Review',
      reviewed: 'Reviewed',
      pending: 'Pending',
      stale: 'Content changed',
      staleItem: 'Changed',
      staleN: '{{n}} to re-check',
      errorsN: '{{n}} errors',
      pitch: 'Pitch',
      pitchHint:
        'A/B the melody against the MR from the same position to judge whether the MR really rings the written melody. Click the ruler to move the playhead.',
      contourNote:
        'The grey curve is the pitch extracted from the MR — a side-check only, and unreliable on polyphonic piano.',
      editorHint:
        'Click empty space to add a note; drag to move or resize. With a note selected, arrows move it (Shift+←/→ resizes) and Delete removes it.',
      play: 'Play',
      stop: 'Stop',
      sourceMelody: 'Melody',
      sourceMr: 'MR',
      melodyEdited: 'Edited',
      saveMelody: 'Save melody to disk',
      saveMelodyDownload: 'Export melody',
      revertMelody: 'Revert to original',
      noNotes: 'No notes — nothing to save',
      analyzingMr: 'Analyzing MR…',
      audio: 'Audio',
      mr: 'MR backing',
      audioMissing: 'No audio',
      guide: 'Guide narration',
      meta: 'Meta',
      importReview: 'Import review',
      exportReview: 'Export review',
      saveReview: 'Save review to disk',
      saveReviewDownload: 'Save review (download)',
      confirmImport:
        'Import {{n}} reviewed items? Verdicts for those items will be overwritten. Continue?',
      indexEdited: 'The curriculum has unsaved changes',
      saveIndex: 'Save changes to disk',
      saveIndexDownload: 'Export changes',
      revertIndex: 'Discard changes',
      newProgram: 'New program',
      untitledProgram: 'Untitled program',
      programName: 'Program name',
      programDesc: 'Description',
      deleteProgram: 'Delete program',
      confirmDeleteProgram: 'Delete this program?',
      addStep: 'Add step',
      stepExercise: 'Exercise',
      stepTime: 'Time',
      stepPurpose: 'Purpose',
      stepUp: 'Move up',
      stepDown: 'Move down',
      stepRemove: 'Remove step',
      mrSpec: 'MR spec — melody generator',
      specMatches: 'Matches the current melody',
      specDiffers: 'Differs from the current melody',
      specSummary: '{{notes}} notes · {{range}}',
      specInvalid: 'Cannot generate — check the pattern and key',
      applyGenerated: 'Replace with generated melody',
      mrAudioStale:
        'The MR spec changed. The existing MR audio was rendered from the old spec, so until it is re-rendered only the melody/MR audition can confirm the match.',
      totalTime: 'Total time',
      minutesN: '{{n}} min',
      bookend: 'Warmup·cooldown',
      stepsResolve: 'Steps resolve',
      steps: 'Steps',
      missingExercise: 'missing exercise',
      reviewItems: 'Review items',
      agree: 'Agree',
      error: 'Error',
      itemNote: 'Note',
      allAgreed: 'All agreed',
      progress: '{{done}}/{{total}}',
      clip: { intro: 'Intro', how: 'How', cue: 'Cue' },
      field: {
        name: 'Name',
        en: 'English name',
        level: 'Level',
        vowels: 'Vowels',
        difficulty: 'Difficulty',
        minutes: 'Minutes',
        key: 'Key (root)',
        effect: 'Effect',
        warn: 'Safety warning',
        pattern: 'Pattern',
        bpm: 'BPM',
        repeats: 'Repeats',
        span: 'Span (semitones)',
        dur: 'Note length (s)',
      },
      item: {
        melody: 'Melody accuracy',
        range: 'Range fit',
        mrMatch: 'MR ↔ melody match',
        guideIntro: 'Guide: intro',
        guideHow: 'Guide: how',
        guideCue: 'Guide: cue',
        audioMeta: 'Audio quality',
        metaFields: 'Meta fields',
        steps: 'Step layout',
        duration: 'Total time',
        bookend: 'Warmup·cooldown',
        safety: 'Safety rules',
      },
    },
    testers: {
      title: 'Testers',
      detailTitle: 'Tester Detail',
      subtitle:
        'Browse testers and validate the diagnosis engine against each capture session.',
      search: 'Search by name or handle...',
      count: '{{count}} testers',
      resetFilters: 'Reset filters',
      empty: 'No testers found',
      emptyHint: 'Try adjusting your search or filters.',
      level: {
        all: 'All',
        expert: 'Expert',
        amateur: 'Amateur',
      },
      gender: {
        all: 'All genders',
        male: 'Male',
        female: 'Female',
      },
      filter: {
        allGenders: 'All genders',
      },
      table: {
        tester: 'Tester',
        level: 'Level',
        gender: 'Gender',
        sessions: 'Sessions',
        joined: 'Joined',
      },
      verdict: {
        all: 'All',
        unreviewed: 'Unreviewed',
        engine_ok: 'Engine OK',
        engine_off: 'Engine off',
        recapture: 'Recapture',
      },
      session: {
        title: 'Sessions',
        empty: 'This tester has no sessions yet.',
      },
      clip: {
        title: 'Recordings',
        low: 'Low',
        high: 'High',
        sustain: 'Sustain',
      },
      report: {
        title: 'Engine report',
        clipsSuffix: ' clips',
        vocalIndex: 'VOCAL INDEX',
        range: 'Range',
        fach: 'Fach',
        vocalAge: 'Vocal age',
        notAnalyzed: 'Not analyzed yet',
        analyze: 'Analyze session',
        reanalyze: 'Re-analyze',
        analyzing: 'Analyzing…',
        agree: 'Agree',
        off: 'Off',
        corrected: 'Corrected',
        placement: {
          velum: 'Velum (resonance)',
          fold: 'Vocal folds (tension)',
          tongue: 'Tongue (muffled)',
          resonance: 'Resonance score',
          warn: 'Warn',
          ok: 'Normal',
        },
      },
      detail: {
        back: 'Testers',
        reviewPanel: 'Review',
        verdictLabel: 'Verdict',
        tagsLabel: 'Tags',
        tagsPlaceholder: 'Type a tag, press Enter (e.g. strained, resonant)',
        noteLabel: 'Reviewer note',
        notePlaceholder: 'Add a note about this session...',
        save: 'Save review',
        reviewedBy: 'Reviewed by',
        reviewedAt: 'Reviewed at',
        unsavedWarning: 'You have unsaved changes. Leave this page anyway?',
      },
      toast: {
        saved: 'Review saved',
      },
    },
    labelHistory: {
      title: 'Label history',
      empty: 'No review history yet.',
      flags: '{{count}} flagged',
    },
    modelQuality: {
      title: 'Model Quality',
      subtitle: 'Engine-vs-human agreement and dataset coverage.',
      stats: {
        agreement: 'Agreement',
        agreementHint: 'across {{count}} judged fields',
        collectingHint: '{{count}} judged — reliability pending',
        flags: 'Judged fields',
        metrics: '{{count}} metrics tracked',
        corrections: 'Corrections',
        correctionsHint: 'fields corrected by reviewer',
      },
      reliability: {
        collecting: 'Collecting',
        insufficient: 'Low reliability',
        detail: '{{n}} judged · {{raters}} raters · κ {{kappa}}',
      },
      metrics: {
        title: 'Per-metric delta',
        agreement: 'Agreement',
        corrections: 'Fixes',
        avgDelta: 'Avg Δ',
      },
      coverage: {
        title: 'Coverage — technique × gender',
        technique: 'Technique',
        male: 'Male',
        female: 'Female',
        total: 'Total',
      },
    },
  },
  landing: {
    nav: {
      goldPrice: 'Gold Price',
      about: 'Business',
      reservation: 'Visit',
      report: 'Report',
    },
    hero: {
      badge: 'Physical Asset Specialists',
      title: 'Redefining the standard',
      titleHighlight: 'of real asset trading.',
      subtitle:
        'Taebum Precious Metals connects the value of precious metals and industrial materials more transparently, powered by refining-based appraisal technology and real-time market data.',
      keyMessage: 'We buy directly, and supply directly.',
      ctaPrimary: 'Check Live Prices',
      ctaSecondary: 'Get Asset Appraisal',
    },
    about: {
      title: 'Built on traditional trust,',
      titleHighlight: 'powered by digital innovation.',
      description:
        'Taebum Precious Metals is a physical asset specialist that establishes transparent trading standards and evaluates customer assets with the most reasonable criteria.',
      descriptionSub:
        'Based on years of accumulated appraisal expertise and market analysis, we go beyond simple transactions to build a structure that provides accurate standards and trust.',
      points: {
        valuation: {
          title: 'Honest Valuation',
          description:
            'We accurately evaluate purity and weight through rigorous assay systems.',
        },
        realtime: {
          title: 'Real-time Pricing',
          description:
            'We provide market-based trading standards reflecting domestic and international prices.',
        },
        customized: {
          title: 'Customized Asset Management',
          description:
            'We offer optimized trading environments for both individuals and businesses.',
        },
      },
      buyRequest: 'Request Purchase',
    },
    differentiators: {
      title: 'Less distribution,',
      titleHighlight: 'higher standards.',
      subtitle: 'This is how we differ',
      items: {
        directPurchase: {
          title: 'Direct Purchase Model',
          description:
            'We offer higher purchase prices by eliminating intermediaries.',
        },
        refiningEvaluation: {
          title: 'Refining-based Evaluation',
          description:
            'We assess asset value more precisely for gold, silver, palladium, copper and more.',
        },
        directSupply: {
          title: 'Direct Supply System',
          description:
            'We connect enterprise clients with reliable raw material supply.',
        },
      },
    },
    businessAreas: {
      title: 'Beyond precious metals,',
      titleHighlight: 'to industrial materials.',
      description:
        'Taebum Precious Metals is expanding beyond simple gold trading to a structure that covers all physical assets.',
      items: {
        purchase:
          'Purchase of pure gold / 18K / 14K / platinum / silver / copper',
        supply: 'Gold bar / Silver bar supply',
        b2b: 'B2B raw material supply for enterprises',
        store: 'Direct store operations',
        online: 'Online platform business',
        production: 'Gold bar / Jewelry product manufacturing',
      },
    },
    platform: {
      subtitle: 'More transparent value, more solid transactions',
      description:
        'Going beyond the closed and complex precious metals trading market, Taebum is preparing a new digital platform so that anyone can trade with clearer information and a more trustworthy structure.',
      bannerTitle: 'A platform for standards, coming soon',
      benefitsTitle: 'The platform changes the market',
      benefits: {
        distribution: {
          title: 'Shortened Distribution',
          description:
            'By eliminating multi-layer distribution and enabling direct trading, sellers get higher prices and buyers get lower prices.',
        },
        data: {
          title: 'Data-driven Pricing',
          description:
            'By publishing real-time prices and trading data, anyone can trade based on accurate market standards.',
        },
        trust: {
          title: 'Verified Safe Trading',
          description:
            "Based on Taebum's appraisal capabilities, we guarantee the quality and trust of every transaction.",
        },
      },
    },
    cta: {
      title: 'Check the value of your assets now.',
      ctaPrimary: 'View Live Prices',
      ctaSecondary: 'Request Purchase Consultation',
      step1: 'Online Purchase Request',
      step2: 'Store Visit & Appraisal',
      step3: 'Asset Growth',
    },
    goldPrice: {
      title: 'Live Gold Price',
      prevDay: 'vs Previous',
      changeRate: 'Change',
      international: 'International',
      domestic: 'Domestic',
      taebum: 'Taebum (Buy)',
      sell: 'Sell (Purchase)',
    },
    report: {
      subtitle: 'Issues at a Glance',
      title: 'Taebum Report',
      viewAll: 'View All',
      readMore: 'Read',
      backToList: 'Back to list',
      otherReports: 'Other Reports',
      allCategory: 'All',
    },
    purchase: {
      title: 'Purchase Consultation',
      description:
        'Please fill in the information below and our team will contact you shortly.',
      name: 'Name',
      namePlaceholder: 'John Doe',
      phone: 'Phone',
      phonePlaceholder: '010-0000-0000',
      item: 'Item',
      itemPlaceholder: 'Select an item',
      items: {
        pureGold: 'Pure Gold (24K)',
        gold18k: '18K Gold',
        gold14k: '14K Gold',
        silver: 'Silver',
        platinum: 'Platinum',
        other: 'Other',
      },
      weight: 'Weight (g)',
      weightPlaceholder: 'e.g. 37.5',
      memo: 'Memo',
      memoPlaceholder: 'Any additional requests',
      submit: 'Submit',
      preparing:
        'Online submission is currently being prepared.\nPlease contact our store directly.',
      purchaseOnlyLabel: 'Buy',
    },
    footer: {
      companyName: 'Taebum Precious Metals',
      copyright: 'All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
    voiceDiagnostic: {
      survey: {
        gender: {
          q: 'What is your vocal base?',
          sub: 'This is your character’s base stat. Your base sets the reference for range and resonance.',
          opts: [
            '👩 Female tone',
            '👨 Male tone',
            '👻 Mystery (I’ll keep it secret)',
          ],
        },
        birthYear: {
          q: 'To measure your vocal-cord age, tell us the year you were born.',
          sub: 'We’ll compare your real age with your “voice age.”',
        },
        tier: {
          q: 'Where does your current vocal tier sit?',
          sub: 'We’ll explain the results at your level.',
          opts: [
            '⭐ Lv.1 · Shower K-pop Star',
            '⭐⭐ Lv.2 · Coin-Karaoke Superstar',
            '⭐⭐⭐ Lv.3 · Local Song Contest Finalist',
            '⭐⭐⭐⭐ Lv.4 · Aspiring Pro',
            '⭐⭐⭐⭐⭐ Lv.5 · Stage-Wrecking Complete Package',
          ],
        },
        wannabe: {
          q: 'Which vocal master do you want to become?',
          sub: 'Pick a target voice and we’ll analyze the gap against it.',
          opts: [
            'Stage-shaking [Powerful Voice]',
            'Dripping with charm [Husky Voice]',
            'Eardrum-melting [Soulful Honey Voice]',
            'Crisp like soda [Clear & Refreshing Voice]',
            'Overwhelming resonance [Pro Classical Voice]',
            'Still exploring! (Not sure yet)',
          ],
        },
        purpose: {
          q: 'What is the main quest (goal) of this vocal training?',
          sub: 'We’ll pick the points to highlight in your results based on your goal.',
          opts: [
            'Escape tone-deafness & off-beat',
            'Crush a wedding song / talent show',
            'Pass an audition / exam',
            'Just enjoy it as a hobby',
          ],
        },
        environment: {
          q: 'Shh! To calibrate the mic, confirm you’re somewhere quiet.',
          sub: 'For accurate analysis, we recommend a room or studio with no background noise.',
          opts: ['✅ It’s quiet', '❌ A bit noisy'],
        },
        condition: {
          q: 'How is your vocal-cord condition (HP) today?',
          sub: 'A low score might just be your condition. We’ll factor it into the results.',
          opts: [
            '🔋 Lv.1 Fully drained (my throat hurts)',
            '😮‍💨 Lv.2 Slightly hoarse',
            '🙂 Lv.3 Average',
            '💪 Lv.4 Good',
            '🌟 Lv.5 Fully charged (straight to the studio!)',
          ],
        },
      },
      stages: {
        low: {
          title: 'Quest 1 · Dig the Tunnel ⬇️',
          cue: 'Imagine you’re a giant cow and ease your voice all the way down to the floor with a “mmm~mweee”',
          safety: 'Don’t strain — only as low as comes out comfortably',
        },
        high: {
          title: 'Quest 2 · Rocket Launch 🚀',
          cue: 'Buzz your nose! Break through to the very top like a siren, “weee↗”',
          safety: '⚠️ Forced high notes risk vocal-cord damage! Never strain',
        },
        sustain: {
          title: 'Quest 3 · Breath Defense 🛡️',
          desc: 'Listen closely to the reference note “A,” then hold a long, steady “Ah—” in one breath without wavering.',
          sub: 'The steadier your pitch, the higher your defense gauge fills.',
          cue: 'Follow the reference note and hold “Ah~” steady to the end',
        },
      },
      measure: {
        step: 'Measure {{step}}',
        micLabel: 'Start recording',
        micHint: 'Press the mic button when you are ready',
        secondsHeld: 'sec held',
        sustainCue: 'Hold “Ah~” as long as you can!',
        inputLevel: 'Input level',
        refListen: 'Listen closely to the reference tone…',
        invalidVoice:
          'No valid vocalization was detected. In the long “Ah—” stage, follow the reference tone and sustain a single steady note.',
        micPermissionDenied:
          'Microphone access was denied. Change the permission for this site to Allow in your browser’s address bar.',
        micUnsupported:
          'This browser does not support microphone analysis. Please use the latest Chrome/Safari.',
        coachingExcellent: 'Great! Hold on to your limit',
        coachingGood: 'Nice — keep it steady to the end!',
        rangeMarkLow: 'Low ',
        rangeMarkHigh: 'High ',
        loadingHeading: 'AI is analyzing your vocal DNA in detail…',
        tipLabel: 'Tip',
        rangeNext: 'Next ▸',
      },
      nav: {
        prev: 'Back',
        next: 'Next',
        start: 'Start measuring',
        yearSuffix: '{{year}}',
      },
      save: {
        sending: 'Sending research data…',
        stored: '✓ Safely stored as research data (consent-based)',
        demo: '✓ Analysis complete · auto-sent once the storage server is connected (demo)',
        offline:
          '✓ Analysis complete · backend not connected (auto-sent once the server is connected)',
      },
      toast: {
        consentRequired: 'Please agree to the [Required] item below first!',
        selectOption: 'Please select this item!',
        linkCopied: 'Copied the result link!',
        linkCopyUnsupported: 'This browser does not support copying links.',
      },
      tips: [
        {
          label: 'Singer’s Formant',
          body: 'The “singer’s formant” is the magic frequency (around 3kHz) that lets your voice cut through a band’s loud accompaniment and reach the audience clearly!',
        },
        {
          label: 'Mix Voice',
          body: 'The “mix voice” blends chest resonance and head resonance — the ultimate skill that keeps your throat from tightening even on high notes.',
        },
        {
          label: 'Vibrato',
          body: 'Vibrato’s golden ratio is a smooth pitch wobble about 5–7 times per second.',
        },
        {
          label: 'High-Note Technique',
          body: 'The key to high notes isn’t clenching your throat — it’s steady breath pressure and a thin, delicate vocal-cord contact.',
        },
      ],
      intro: {
        titleLead: 'MUZLY AI will ',
        titleHighlight: 'diagnose your vocals',
        titleTail: '.',
        subLine1: 'With a short survey and a 3-step voice measurement,',
        subLine2: 'we analyze your range, tone, and phonation as data.',
        consentRequired: '[Required]',
        consentBody:
          ' A microphone recording is made for the diagnosis, and I agree that MUZLY may securely store and use this recording and the analysis metrics for voice-AI research and model improvement. ',
        consentLink: 'See how research data is used',
        start: 'Start vocal diagnosis →',
      },
      voiceTest: {
        eyebrow: 'Live Demo · AI Vocal InBody',
        titleLead: 'My voice, ',
        titleHighlight: 'read as data',
        titleTail: '',
        titleLine2: 'from range to tone, all at once',
        body: 'After a short survey, we measure 3 steps — lowest note, highest note, and sustained phonation. AI analyzes your range, voice part, pitch, tone, phonation, and singer’s formant to build your own Vocal InBody report.',
        cardTitle: 'AI Voice Diagnosis',
      },
      result: {
        rankTop: 'Estimated top',
        rankSub: 'AI-estimated percentile (beta)',
        replay: 'Replay my recording',
        betaLabel: 'Beta notice ·',
        betaBody:
          'The current diagnostic metrics are still in development, so results may not be accurate. Based on voice data collected with consent, MUZLY will continuously train and refine its analysis engine to evolve toward ever more precise diagnoses.',
        share: 'Share result with a friend',
        retry: 'Measure again',
      },
      grade: {
        s: 'S · Professional',
        a: 'A · Excellent',
        b: 'B · Great',
        c: 'C · Developing',
        d: 'D · Beginner',
        desc: '{{voice}} voice · {{character}} tone. A vocal index combining six metrics.',
      },
      metrics: {
        pitchAccuracy: {
          label: 'Pitch accuracy',
          desc: 'Avg {{cents}} cents off reference A',
        },
        pitchStability: {
          label: 'Pitch stability',
          desc: 'Pitch wobble (std dev) {{cents}} cents',
        },
        sustain: {
          label: 'Breath support',
          desc: 'Steady phonation for ~{{sec}}s',
        },
        vibrato: {
          label: 'Vibrato',
          desc: 'Regularity of pitch oscillation',
        },
        resonance: {
          label: 'Resonance',
          desc: 'Resonance from harmonic-to-noise ratio (HNR)',
        },
        singersFormant: {
          label: "Singer's formant",
          desc: '2.8–3.4kHz overtone energy (operatic ring)',
        },
      },
      voiceType: {
        label: '{{tone}} {{range}}',
        tone: {
          warm: 'Warm',
          balanced: 'Balanced',
          bright: 'Bright',
        },
        range: {
          low: 'Low',
          midLow: 'Mid-low',
          midHigh: 'Mid-high',
          high: 'High',
        },
      },
      detail: {
        head: 'Detailed Vocal Report',
        preciseBadge: 'Precise analysis applied',
        rangeLabel: 'Your vocal range',
        rangeSub: 'About {{semitones}} semitones ({{low}}–{{high}} Hz)',
        fach: 'Voice part',
        fachValues: {
          soprano: 'Soprano',
          alto: 'Alto',
          tenor: 'Tenor',
          bass: 'Bass',
          soprano_tenor: 'Soprano–Tenor range',
          alto_bass: 'Alto–Bass range',
        },
        voiceAge: 'Voice age',
        ageUnit: ' yrs',
        actualAge: 'Actual {{age}} yrs',
        character: 'Tone',
        characterValues: {
          breathy: 'Husky',
          pressed: 'Pressed & powerful',
          clear: 'Clear & pure',
          bright: 'Bright & crisp',
          balanced: 'Balanced',
          warm: 'Warm & soft',
        },
        similarArtists: 'Artists with a similar range',
      },
      resonance: {
        title: 'Resonance & Placement Diagnosis',
        aria: 'Vocal-tract resonance diagnosis',
        points: {
          hardPalate: 'Hard palate',
          lips: 'Lips',
          tongue: 'Tongue',
          softPalate: 'Soft palate',
          vocalFolds: 'Vocal folds',
        },
        notes: {
          velum: {
            title: 'Soft palate',
            desc: 'The soft palate isn’t opening enough, so resonance isn’t carrying upward.',
          },
          fold: {
            title: 'Vocal folds',
            desc: 'Tension is concentrating on your vocal folds.',
          },
          tongue: {
            title: 'Tongue',
            desc: 'The back of the tongue is pressed down, so the sound may seem a bit muffled.',
          },
          balanced: {
            title: 'Balanced resonance',
            desc: 'Your resonance placement is fairly stable. Try to maintain this balance.',
          },
        },
      },
      register: {
        title: 'Head·Mix·Chest Resonance Ratio',
        head: 'Head',
        mix: 'Mix',
        chest: 'Chest',
        headResonanceLabel: 'Head resonance',
        chestResonanceLabel: 'Chest resonance',
      },
      singersFormant: {
        title: 'Singer’s Formant Map',
        aria: 'Harmonic spectrum and singer’s formant',
        bandLabel: 'Singer’s formant ≈3kHz',
        cap: 'The more the harmonics of the fundamental F0 {{f0}}Hz cluster around the ≈3kHz band, the stronger the “singer’s formant.” Formation {{score}}/100',
        capSpr: ' · SPR {{spr}}dB (lower is stronger)',
        capFormants: ' · F3 {{f3}}Hz · F4 {{f4}}Hz',
        capFormantsPending: ' · Analyzing precise F3·F4…',
      },
      engineMap: {
        title: 'Analysis Engine Mapping',
        openSource: 'Open source',
        desc: 'We transparently disclose which voice-analysis tool actually measures each metric.',
        rows: [
          {
            metric: 'Voiced-segment detection',
            desc: 'Remove silence/noise and extract only valid phonation',
          },
          {
            metric: 'Range & pitch (F0)',
            desc: 'Lowest/highest notes, pitch accuracy & stability',
          },
          {
            metric: 'Resonance·tone·quality·formants',
            desc: 'Formants F1–F4 · HNR · jitter/shimmer · CPPS',
          },
          {
            metric: 'Head·mix·chest (phonation mode)',
            desc: 'Spectral tilt · H1−H2 (glottal open quotient)',
          },
          {
            metric: 'Singer’s formant (≈3kHz)',
            desc: 'LTAS long-term average spectrum · SPR (Singing Power Ratio)',
          },
          {
            metric: 'Vibrato·spectrum',
            desc: 'Pitch wobble (4–9Hz) · spectral centroid',
          },
          {
            metric: 'Real-time live pitch',
            desc: 'In-browser autocorrelation (ACF) during measurement',
          },
        ],
      },
      badge: {
        estimated: 'Estimated',
      },
    },
  },
  web: {
    common: {
      // 설문 단계의 CTA. 안내 화면의 `다음`과 갈라 둔다 — 답하는 화면과
      // 읽고 넘어가는 화면은 같은 문구를 쓰면 안 된다(시안).
      continue_: 'Continue',
      appName: 'MUZLY',
      // Shared by the survey shell and the history header — one wording per action.
      back: 'Back',
      next: 'Next',
      // Accessible name for the survey shell's progress bar (WCAG 4.1.2) — the
      // fill itself is visual-only, so screen readers need this text instead.
      progress: 'Step {{current}} of {{total}}',
    },
    offline: {
      offline: "You're offline",
      recovered: 'Back online',
    },
    // Share-link gateway (`/link?to=`) — shown after a mobile browser tries to open the app.
    link: {
      opening: 'Opening the MUZLY app',
      hint: "If the app doesn't open, you can continue on the web.",
      continueWeb: 'Continue on the web',
    },
    // Shared result (`/share/:id`) — what someone sees after opening a result link.
    share: {
      title: 'Vocal diagnosis result',
      loading: 'Loading the result',
      unavailableTitle: "This result isn't available",
      unavailableBody:
        'The link may be wrong, or the shared result may have been deleted.',
      cta: 'Try your own diagnosis',
    },
    // Canonical **names and caveats** for the scored axes (metricGrades). The
    // home summary, trend toggle, result cards and history all read these — one
    // number under two names reads as two different metrics.
    metrics: {
      grade: {
        solid: 'Solid',
        proxy: 'Proxy',
        weak: 'Weak evidence',
      },
      sustain: {
        label: 'Breath support',
        note: 'Measures how long you held one breath. The score is our own scale with 12 seconds as full marks, so it uses a different yardstick from the reference band.',
      },
      pitchAccuracy: {
        label: 'Pitch accuracy',
        note: 'Measures how far each note strayed from the nearest semitone. It is not matched against a set melody, so any steady note scores well.',
      },
      pitchStability: {
        label: 'Pitch stability',
        note: 'Measures how much your pitch wavered.',
      },
      vibrato: {
        label: 'Vibrato',
        note: 'Our own way of counting regular pitch oscillation. There is no validated benchmark yet, so treat it as a hint.',
      },
    },
    // One note's spelling — canonical. Korean readers learn solfège, so ko puts it first
    // (`라♯2`); English keeps the letter name only. Screens reach it via `app/core/noteLabel`.
    note: {
      label: '{{note}}',
    },
    language: {
      label: 'Language',
      // Language names stay in their own locale in every bundle
      ko: '한국어',
      en: 'English',
      zh: '中文',
    },
    tabs: {
      home: 'Home',
      more: 'MY',
    },
    // Terms / privacy screens. The body is not here — it lives in per-effective-date document files
    // (`apps/web/src/app/features/policy/documents`).
    policy: {
      terms: { title: 'Terms of service' },
      privacy: { title: 'Privacy policy' },
      version: 'Effective date',
      language: 'Language',
      summaryTitle: 'What we collect at a glance',
    },
    // MY tab (Figma `MY #메인`) — profile card · vocal report · settings list.
    more: {
      title: 'MY',
      profile: 'Edit profile',
      together: 'Day {{count}} together',
      report: 'My vocal report',
      privacy: 'Privacy policy',
      version: 'App version',
      versionWeb: 'v{{web}}',
      versionNative: 'v{{app}} ({{build}}) · web v{{web}}',
      guest: 'Guest',
      stats: {
        runs: 'Runs',
        runsUnit: '',
        latest: 'Latest score',
        latestUnit: 'pts',
        range: 'My range',
        none: '—',
        noneLabel: 'Not yet',
        emptyHint:
          'Finish your first diagnosis to see your score and range here.',
      },
      groups: {
        settings: 'Settings',
        support: 'Support',
        legal: 'Legal',
        // Opened only by tapping the app version — otherwise absent from the list.
        debug: 'Developer options',
      },
      items: {
        language: 'Language',
        faq: 'FAQ',
        contact: 'Contact us',
        feedback: 'Leave feedback',
        deleteData: 'Delete all diagnosis data',
        terms: 'Terms of service',
        licenses: 'Open-source licenses',
        probe: 'Capture probe overlay',
        debugConsole: 'Engine debug console',
        lockDebug: 'Lock developer options',
      },
      // MY → Delete all diagnosis data. The server soft-deletes, so no "permanent"/"cannot be undone".
      deleteData: {
        title: 'Delete all diagnosis data?',
        body: 'This deletes the diagnosis records kept on our server and clears the diagnosis history on this device. Your profile stays.',
        confirm: 'Delete',
        cancel: 'Cancel',
        done: 'Diagnosis data deleted',
        partial: "Some data couldn't be deleted. Please try again",
        failed: "Couldn't delete. Please try again in a moment",
      },
      // Language screen (Figma `MY #언어`). Names are written in the current UI language; native names live in `web.language.*`.
      language: {
        title: 'Language',
        desc: 'Choose the language for the app.',
        done: 'Done',
        options: {
          ko: 'Korean',
          en: 'English',
          zh: 'Chinese',
        },
      },
      debugUnlocked: 'Developer options unlocked',
      // Open-source licenses screen — the list is data (`features/more/licenses.ts`); full texts stay in English.
      licenses: {
        intro:
          'The open-source software MUZLY uses.\nEach group ends with its full license text.',
        jump: 'Jump to a license',
        count_one: '{{count}} package',
        count_other: '{{count}} packages',
        show: 'Read the {{name}}',
        hide: 'Hide license text',
      },
      contactCta: 'Email us',
      contactMail: {
        subject: '[MUZLY Support]',
        info: 'Please keep the details below — they help us look into the issue.',
        os: 'OS: {{os}}',
        failed: "Couldn't open a mail app. Please email {{email}}",
        failedCopied:
          "Couldn't open a mail app. {{email}} is copied to your clipboard",
      },
      // FAQ and contact copy. Terms and privacy bodies are not here (see `web.policy`). The screen never
      // says "placeholder": App Store review (2.1) reads that as unfinished.
      docs: {
        faq: {
          title: 'FAQ',
          // Order lives in `FaqPage`'s `FAQ_IDS` — add an item there and in both locales.
          items: {
            frequency: {
              q: 'How often should I run a diagnosis?',
              a: 'Once or twice a week, at a similar time and place, makes change easy to see.',
            },
            variance: {
              q: 'My results differ every time.',
              a: 'Throat condition, background noise and mic distance all move the numbers. Try a quiet room.',
            },
            score: {
              q: 'What does the overall score mean?',
              a: 'It is the average of the axes that were measured. The weights between axes are not calibrated yet, so compare it with your own earlier runs rather than with other people.',
            },
            mic: {
              q: 'The microphone won\u2019t turn on.',
              a: 'Turn on the microphone for MUZLY in your device settings. Come back and you can carry on from the step you stopped at.',
            },
            storage: {
              q: 'Where do recordings go?',
              a: 'Your runs stay on your device, and the analysis results and recordings are also kept on our servers so you can play a run back.',
            },
            delete: {
              q: 'How do I delete my data?',
              a: "Go to MY → Delete all diagnosis data to delete the diagnosis records kept on our server and clear the diagnosis history on this device. To delete a single diagnosis, tap the delete button at the top right of its result screen. For anything the app can't do, such as deleting your profile, email support@muzly.io.",
            },
          },
          notFound: "Didn't find your answer?",
          contact: 'Contact us 1:1',
        },
        contact: {
          title: 'Contact us',
          body: 'Tell us what is confusing or broken. We will get back to you as soon as we can.',
        },
      },
    },
    // Feedback — one type plus a free-text note. Nobody replies here (support is `more.docs.contact`).
    feedback: {
      title: 'Leave feedback',
      close: 'Close',
      // Home prompt — shown once to people who actually looked at their first result.
      prompt: {
        title: 'How has MUZLY been\nfor you so far?',
        body: 'What you liked, what fell short — both help.\nWe will use your feedback\nto make MUZLY better.',
        cta: 'Leave feedback',
        later: 'Maybe later',
      },
      type: {
        title: 'How was your experience?',
        subtitle: 'Pick the one that fits best.',
        good: { label: 'Good', desc: 'I was happy with it.' },
        okay: { label: 'Okay', desc: 'It was fine overall.' },
        bad: { label: 'Not great', desc: 'It needs some work.' },
      },
      detail: {
        title: 'Tell us more!',
        subtitle: 'What worked, what needs work —\nanything helps.',
        label: 'Your feedback',
        placeholder: 'Write your answer here.',
        notes: {
          purpose: 'We use your feedback only to improve the service.',
          noReply: 'We do not reply to feedback individually.',
        },
      },
      submit: 'Submit',
      submitted: 'Feedback sent',
      leave: {
        title: 'Stop leaving feedback?',
        body: 'Leave without sending your feedback?\nYou can come back any time from MY.',
        stop: 'Stop',
        stay: 'Keep going',
      },
    },
    home: {
      diagnosis: {
        section: 'Vocal diagnosis',
        // The step count is read from RECORD_TESTS by the screen.
        subtitle: 'A {{count}}-step diagnosis\nto hear what your voice can do',
        start: 'Start diagnosis',
        resume: 'Continue diagnosis',
        duration: 'About {{value}} min',
      },
      // Resume popup (Figma `1048:48431`) — shown on entering home with a run in progress.
      resume: {
        title: 'You have a saved diagnosis',
        // The step "in progress" = finished tests + 1. Denominator from RECORD_TESTS.
        status: 'Step {{current}}/{{total}} in progress',
        body: 'Pick up from the last step you reached\nor start over from the beginning',
        restart: 'Start over',
        // Naming the loss in the label wrapped badly inside the button — say it in the body.
        restartWarning:
          'Starting over discards the {{current}} step(s) you have measured',
        continue_: 'Continue',
        dismiss: 'Close',
      },
      recent: {
        section: 'Latest diagnosis',
        overall: 'Overall score',
        score: '{{value}} / 100',
        uncalibrated:
          'The overall score is an unweighted mean of the measured axes (uncalibrated).',
        all: 'See full results',
      },
      // The seven metric tiles (Figma §7.1) — names come from the detail tabs.
      tiles: {
        section: 'Latest result',
        // Tiles are narrow — tighter than the detail screen's `E2 ~ C5` (Figma §7.1).
        range: '{{low}}-{{high}}',
        hz: '{{value}}Hz',
        // The session date on the header's right (Figma `2026. 00. 00 >`) — links to history.
        date: '{{year}}. {{month}}. {{day}}',
      },
      trend: {
        section: 'Recent vocal trend',
        axisLabel: 'Trend axis',
        axis: {
          overall: 'Overall score',
          breath: 'Breath stability',
          range: 'Range',
          pitchAccuracy: 'Pitch accuracy',
        },
        axisShort: {
          overall: 'Overall',
          breath: 'Breath stability',
          range: 'Range',
          pitchAccuracy: 'Pitch accuracy',
        },
        semitones: '{{value}} semitones',
        // Period select (Figma §7.1). The number goes into the sentence.
        period: 'Last {{value}} days',
        periodLabel: 'Trend window',
        outOf: '/ 100',
        // Against the first value in the window, not the previous session.
        deltaUp: '{{value}} better',
        deltaDown: '{{value}} lower',
        deltaUpSemitones: '{{value}} semitones better',
        deltaDownSemitones: '{{value}} semitones lower',
        // Days, not sessions — the series is aggregated per day, so two takes on
        // the same day still plot as one point.
        empty: 'Trends show up once you have diagnosed on two different days',
        // The window is selectable (7/30 days) — a hardcoded count lies on the 30-day one.
        alt: '{{axis}} over the last {{days}} days — last measured {{date}}, {{value}}',
        altEmpty: '{{axis}} over the last {{days}} days — nothing measured',
      },
    },
    profile: {
      // Profile edit (Figma `MY #프로필 수정`).
      title: 'Edit profile',
      groups: {
        basic: 'Basics',
        vocal: 'Vocal',
      },
      nickHelp: 'Use {{min}}–{{max}} characters.',
      // Age shown on the closed row — the option list also names the birth year.
      ageValue: '{{age}}',
      note: 'Gender and age set the baseline for range and resonance analysis.\nChanges apply from your next diagnosis.',
      unanswered: 'Not answered yet',
      save: 'Save',
      unsavedConfirm: 'You have unsaved changes. Leaving will discard them.',
      saved: 'Saved',
      // The survey asks these as full questions ("How deep into singing are
      // you?"); a list needs the name of the thing being set, not the question.
      fields: {
        gender: 'Gender',
        birthYear: 'Age',
        experience: 'Vocal experience',
        style: 'Vocal style you want',
      },
      // Profile photo. It stays on this device and never reaches the server —
      // say so, or people won't expect it to disappear when they switch phones.
      photo: {
        label: 'Profile photo',
        alt: 'Your profile photo',
        empty: 'No photo',
        change: 'Change photo',
        add: 'Add photo',
        camera: 'Take a photo',
        library: 'Choose from library',
        remove: 'Remove photo',
        cancel: 'Cancel',
        localOnly: 'Your photo stays on this device.',
        denied: 'Turn on photo access in Settings to use this.',
        failed: "Couldn't load that photo. Please try again.",
      },
    },
    // 시안의 분홍 캐릭터. 여러 화면이 같은 그림을 쓴다.
    brand: {
      mascotAlt: 'MUZLY mascot',
    },
    onboarding: {
      // The copy owns its line breaks — the screen renders with whitespace-pre-line.
      // The free-text field that appears after picking "other" — same copy in
      // every survey step.
      other: {
        placeholder: 'Type your answer',
        label: 'Other — type your answer',
      },
      nick: {
        title: 'Pick the nickname and language you will use in MUZLY',
        subtitle: 'You can change your nickname anytime in settings.',
        label: 'Nickname',
        placeholder: 'Enter a name',
        // The design's `Description` slot. Without it the cap reads as a bug.
        hint: 'Up to {{max}} characters',
        // Later step titles address the user by name — fallback for empty drafts
        fallback: 'friend',
      },
      gender: {
        title: 'First, let us set the baseline for your voice',
        subtitle: 'Range and resonance baselines differ by gender.',
        options: {
          female: 'Female',
          male: 'Male',
          none: 'Prefer not to say',
        },
      },
      birthYear: {
        title: 'Tell us your age, {{nick}}.',
        subtitle: 'We need it for an accurate analysis.',
        placeholder: 'Select year',
        option: '{{value}} (age {{age}})',
      },
      experience: {
        title: 'How far have you gone with singing, {{nick}}?',
        options: {
          first: 'This is my first time',
          hobby: 'Only as a hobby',
          lesson: 'I have taken lessons',
          pro: 'I am at a pro level',
        },
      },
      style: {
        title: 'What kind of voice do you want?',
        subtitle: 'Pick the style you admire and we will compare against it.',
        options: {
          powerful: 'Powerful vocals',
          ballad: 'Emotional ballad',
          pop: 'Bright pop',
          classic: 'Classical · musical',
          jazz: 'Jazz · soul',
        },
      },
      purpose: {
        title: 'What brings you here today, {{nick}}?',
        options: {
          curious: 'Just curious',
          check: 'To check my level',
          track: 'To track steady growth',
          audition: 'Preparing for an audition or exam',
        },
      },
      environment: {
        title: 'Is it quiet where you are?',
        subtitle: 'Background noise can shake the analysis.',
        options: {
          quiet: 'Quiet indoors',
          noisy: 'Noisy indoors',
          outdoor: 'Outdoors',
        },
      },
      condition: {
        title: 'How does your throat feel today?',
        options: {
          best: 'Great',
          normal: 'Normal',
          husky: 'A bit hoarse',
          bad: 'Rough',
        },
      },
      // The beat that says the survey is done (design `758:20959`).
      surveyDone: {
        title: 'Your vocal setup\nis complete!',
        banner: 'Shall we hear your voice now?',
      },
    },
    diagnosis: {
      // Leaving keeps the saved progress — home offers "continue" instead.
      close: 'Leave diagnosis',
      // The screen counts tests from RECORD_TESTS — freezing the number in the
      // copy would drift from the screen as tests are added.
      intro: {
        testsTitle: 'The vocal diagnosis runs {{value}} voice tests',
        durationTitle: 'The vocal diagnosis takes about {{value}} minutes',
        durationSubtitle:
          'You do not have to sing well\nJust make a sound comfortably',
        slides: 'Slide {{current}} / {{total}}',
        prev: 'Previous slide',
        next: 'Next slide',
        cta: 'Start diagnosis',
      },
      guide: {
        preview: 'Preview the melody',
        previewStop: 'Stop preview',
        previewTone: 'Hear the reference note',
        previewToneStop: 'Stop the reference note',
        earphones:
          'Wear earphones — the backing track won’t leak into your voice, so the result is more accurate',
        step: '{{current}} / {{total}}',
        duration: 'Takes: {{value}}',
        unlimited: 'No limit',
        seconds: '{{value}}s',
        start: 'Start the test',
      },
      tests: {
        sustain: {
          title: 'Baseline phonation',
          summary: 'Match the reference note A and hold an "ah"',
          desc: 'Listen to the reference note A, then hold that note on "ah" — any octave that feels comfortable — for as long as your breath lasts.',
        },
        low: {
          title: 'Low range',
          summary: 'Go as low as you comfortably can',
          desc: 'Slide slowly down to the deepest note you can reach.',
        },
        high: {
          title: 'High range',
          summary: 'Go as high as you comfortably can',
          desc: 'Ease up to your highest note without straining.',
        },
        song: {
          title: 'Sing along',
          summary: 'Sing back the melody we play for you',
          desc: 'A short melody will play. Sing it back at the same pitch and timing — humming on "ah" is fine.',
        },
      },
      segments: {
        sustain: {
          title:
            'Match the reference note A and hold an "ah" for as long as you can.',
          subtitle:
            'Stretch it out comfortably, and tap Done when the sound breaks.',
          upNext: 'Now hold a long "ah" on the reference note A',
        },
        low: {
          title: 'Slide down to the lowest note you can sing on "ah".',
          subtitle: 'One note at a time. Tap Done when you cannot go lower.',
          upNext: 'Now go for your lowest note',
        },
        high: {
          title: 'Now climb to the highest note you can sing comfortably.',
          subtitle: 'Stop if your throat hurts. No need to push.',
          upNext: 'Now go for your highest note',
        },
        song: {
          title: 'Sing back the melody you hear.',
          subtitle:
            'Match both the notes and the timing. It is fine to miss a part.',
          upNext: 'Now sing the melody back',
        },
      },
      track: {
        // Sustain timeline — fills where you held the reference note; the marker rides
        // above or below that note.
        label: 'Timeline that fills while you hold the reference note',
        traceLabel: 'Pitch height over time',
        gaugeLabel: 'How high the note you are singing is',
        vowel: 'Ah',
        sharpBy: '+{{value}} cents',
        flatBy: '−{{value}} cents',
        currentNote: 'Current note',
        tick: {
          high: 'High',
          mid: 'Mid',
          low: 'Low',
        },
      },
      countdown: {
        announce: 'Starting in {{value}}',
      },
      // Fullscreen shown when room noise makes the take impossible (Figma `884:9858`).
      noisyRoom: {
        title: 'Room noise is making\nthis measurement unreliable.',
        banner: 'Try again somewhere quiet for a more accurate result!',
        retry: 'Measure again',
      },
      record: {
        start: 'Start recording',
        finish: 'Finish recording',
        finishLabel: 'Done',
        noVoiceYet: "We haven't picked up your voice yet",
        replayDisabled: "You can't replay the melody while recording",
        recording: 'Recording',
        elapsed: '{{value}}s',
        // A limit is a deadline, not a gauge — say what is left.
        remaining: '{{value}}s left',
        // The big timer's screen-reader name. The design hides this label (1677:17622
        // is off), but a bare number gives assistive tech nothing to name it by, so it
        // stays as sr-only text.
        elapsedLabel: 'Elapsed',
        // What the mic is hearing right now — about sound, not about progress.
        listening: {
          idle: 'Hold it as long as feels comfortable',
          hearing: "We're hearing you clearly",
          // Sound is coming through but it is quiet for this room — the fix is "louder".
          faint: 'That was faint — please sing a little louder.',
          silent: "We can't hear you — try making a relaxed sound.",
        },
        // 노래 진단은 멜로디가 끝나면 기계가 끊는다 — 그 동안 완료 버튼 자리는
        // 누를 것이 아니라 상태다(시안 `1017:17872`).
        measuring: 'Measuring',
        waveformLabel: 'Listening for your voice',
        backingLabel: 'Backing track position',
        // Pause — nothing is kept while paused (useVoiceCapture.pause).
        pause: 'Pause',
        resume: 'Resume',
        restart: 'Start over',
        // The song take was cut by an audio-session interruption. Singing on
        // would land every frame on the wrong note (the scoring anchor is when
        // the backing track started), so the only way out of this take is to
        // start it over — the time limit no longer ends it.
        interrupted: 'The recording was cut off',
        interruptedHint:
          "This take no longer lines up with the backing track. Let's sing it from the top.",
      },
      // The ends of a range. The attempt summary ("Lowest A2") and the graph's
      // reach badge ("Lowest C3") are the **same sentence**, so they share a key
      // — only the note spelling differs.
      bound: {
        low: 'Lowest $t(web.note.label)',
        high: 'Highest $t(web.note.label)',
      },
      // One line on what the last attempt produced. The pure helpers
      // (describeAttempt/attemptWarning) return keys and the screen translates.
      attempt: {
        noSound: "We didn't pick up any sound",
        // No bound at all means this test left nothing behind.
        noRange: "We couldn't hear a pitch, so no range came out of this take",
        sustain: '{{value}}s of voice',
        unstable:
          'Your pitch wavered, which makes analysis harder. Try holding one steady note.',
      },
      pitchGraph: {
        label: 'Live pitch',
      },
      song: {
        loading: 'Getting the melody ready',
        // Losing the audio only costs this one test — the other two are measured.
        loadFailed:
          'We could not load the melody. Try again, or skip just this test — you will still get results.',
        retry: 'Try again',
        skip: 'Skip this test',
        // Say how many notes were sung next to the score — "100%" on half the
        // melody reads as a full take otherwise.
        result: 'Pitch accuracy {{accuracy}}% · {{sung}} of {{total}} notes',
        // No sound to compare — this is not 0%.
        unmeasured: 'We could not hear anything to compare',
        // An unscored take asks for a decision, not applause.
        unmeasuredHint: 'This take will not produce a pitch-accuracy score',
        headphoneAdvice: {
          title: 'Please sing with earphones',
          body: 'When the backing track plays through the speaker, the mic hears it too and your pitch is measured less accurately.\nWired or Bluetooth earphones both work.',
          confirm: 'Got it',
        },
        tendency: {
          low: {
            flat: 'The lower notes mostly sat below pitch',
            sharp: 'The lower notes mostly sat above pitch',
          },
          high: {
            flat: 'The higher notes mostly sat below pitch',
            sharp: 'The higher notes mostly sat above pitch',
          },
        },
      },
      review: {
        heading: 'This measurement',
        testDone: '<{{test}}> is done',
        nextTestHint: 'Let us move on to the next one!',
        lastTestHint: 'Now let us analyse your results!',
        attempts: '{{value}} tries — we keep your best one',
        retry: 'Redo',
        confirm: 'OK',
      },
      // One test finished, fullscreen (Figma `884:9451`).
      stepDone: {
        title: 'Step {{step}} {{test}}\nis done',
        retry: 'Measure again',
        next: 'Next',
        // Capsule shown when the take carries a warning — asks for a decision, not applause.
        warnHint: 'You can carry on, but measuring again will be more accurate',
        finishing: 'Wrapping up…',
      },
      // Everything finished (Figma `893:13968`).
      allDone: {
        title: "That's every test done!",
        body: 'Nicely done.\nWe built your personal result\nfrom what you just sang!',
        result: 'See the result',
      },
      done: {
        title: '{{value}} tests done',
        result: 'See results',
        retry: 'Record again',
      },
      error: {
        title: {
          denied: 'We cannot use the microphone',
          blocked: 'We cannot use the microphone',
          invalid: 'This take was not saved',
          unsupported: 'We cannot start recording right now',
        },
        denied:
          'We could not start the measurement — the microphone is unavailable.',
        blocked:
          'The microphone is off in Settings. Turn it on and you can carry on from here.',
        unsupported: 'This device does not support voice recording.',
        invalid: 'The “ah” in baseline phonation was not steady enough.',
        keptNote: 'The steps you have already measured are still here.',
        invalidNote:
          'We keep the other steps and redo only baseline phonation.',
        openSettings: 'Open Settings',
        retrySustain: 'Redo baseline phonation',
        restartAll: 'Start over',
        mockInvalid:
          'In the browser we analyse a synthetic voice instead of your microphone — it is not a real measurement, so the analysis cannot finish. Please run the real diagnosis in the app.',
        retry: 'Record again',
      },
    },
    history: {
      title: 'Past results',
      intro: 'Each run is compared against the one before it.',
      emptyTitle: 'No runs saved yet',
      // Period filter left nothing — runs do exist.
      emptyPeriod: 'No runs in this period',
      // The overall score on a run card (design `78점`).
      score: '{{value}} pts',
      // Dev-environment caveats are not user copy.
      emptyBody: 'Runs pile up here once you finish a diagnosis.',
      restart: 'Start a new diagnosis',
      // The date that labels a run. Date fields go into a locale format string
      // rather than Intl, so the label does not shift with the timezone.
      date: '{{month}}/{{day}}',
      // Breath-hold seconds are the **same value** the result screen renders, so
      // they share its key (`web.result.sustain.seconds`). A second key here would
      // let one number carry two unit spellings across screens.
      // Change against the previous run. The sign is already on the value ("+2.1").
      delta: {
        sec: '{{value}}s',
        point: '{{value}}pt',
      },
    },
    result: {
      title: 'Your vocal results',
      // Header of the per-session detail screen (Figma §8.2).
      share: {
        label: 'Share result',
        confirmTitle: 'Share this result?',
        confirmBody: 'Anyone with the link can see this diagnosis result.',
        confirm: 'Share',
        cancel: 'Cancel',
        text: 'My MUZLY vocal diagnosis: {{score}} points! Try diagnosing your voice too',
        textNoScore:
          'Check out my MUZLY vocal diagnosis! Try diagnosing your voice too',
        copied: 'Link copied',
        failed: "Couldn't share. Please try again in a moment",
      },
      detail: {
        title: 'Your result',
      },
      // Delete this session, from the detail header.
      delete: {
        label: 'Delete this diagnosis',
        title: 'Delete this diagnosis?',
        body: "This deletes this session's record kept on our server and removes it from the history on this device.",
        confirm: 'Delete',
        cancel: 'Cancel',
        done: 'Diagnosis deleted',
        failed: "Couldn't delete. Please try again in a moment",
      },
      subtitle: 'We only show what we actually measured in your recording.',
      // Vocal fatigue (Figma `921:5549`) — the four axes are the survey's `condition`
      // options, and each value averages the takes reported at that condition. The tab
      // stands only once two axes hold a value (`visibleTabs`).
      fatigue: {
        radarLabel:
          'Vocal fatigue radar, {{filled}} of four conditions measured',
        section: 'Vocal fatigue',
        note: 'How your scores differed by the condition you reported. The wider the gap between good days and bad ones, the more your voice follows your condition.',
        // Avoids a plural form: "1 takes" is what `{{takes}} takes` gives on a
        // single-take axis, and the badge must stay short (Pill does not wrap).
        badge: '{{score}} · ×{{takes}}',
        empty: 'Not yet',
        axis: {
          best: {
            label: 'At its best',
            note: 'Average of days you felt at your best',
            prompt: 'Record on a good day to fill this in',
          },
          normal: {
            label: 'Typical',
            note: 'Average of your ordinary days',
            prompt: 'Record on an ordinary day to fill this in',
          },
          husky: {
            label: 'Slightly hoarse',
            note: 'Average of days your throat felt a little tight',
            prompt: 'Record when slightly hoarse to fill this in',
          },
          bad: {
            label: 'Off day',
            note: 'Average of days your throat felt rough',
            prompt: 'Record on an off day to fill this in',
          },
        },
      },
      pending: 'Not captured this time',
      uncalibrated: 'Uncalibrated scale',
      score: '{{value}} / 100',
      mock: 'In the browser we analyse a synthetic voice instead of your microphone. This result is not saved.',
      failed: {
        title: 'We could not analyse this',
        body: 'There was not enough voice to work with. Please record again.',
        retry: 'Record again',
      },
      env: {
        noisy:
          'It was noisy around you during the test — treat this result as a rough guide',
      },
      condition: {
        best: 'You said your voice felt great today — this is close to your current best.',
        normal: 'You said your voice felt normal today.',
        husky:
          'You said your throat felt slightly hoarse — clarity and high notes can read lower than usual.',
        bad: 'You said your voice was off today — the numbers may look different on a better day.',
      },
      sustain: {
        section: 'Test 1. Hold an “ah”',
        breath: 'Breath support',
        seconds: '{{value}}s',
        band: {
          short:
            'A little shorter than the 15–20s average for your age and gender',
          inBand: 'Within the 15–20s average for your age and gender',
          long: 'Longer than the 15–20s average for your age and gender',
        },
        stability: 'Steadiness',
        clarity: 'Clarity',
        clarityLevel: { low: 'Breathy', mid: 'Average', high: 'Clear' },
        clarityNote: 'Less breath noise in the tone reads as a clearer voice.',
        resonance: 'Resonance',
        resonanceLevel: { low: 'Plain', mid: 'Average', high: 'Rich' },
        resonanceNote: 'A bit of ring carries your voice further.',
      },
      tabs: {
        label: 'Metric details',
        footnote:
          'Results shift with your surroundings and how you feel that day.',
        name: {
          fatigue: 'Vocal fatigue',
          range: 'Range',
          registers: 'Pitch',
          vibrato: 'Vibrato',
          breath: 'Breath',
          formant: "Singer's formant",
          resonance: 'Resonance shape',
        },
        // Tab header subtitle under the title (design §8.3–§8.9).
        subtitle: {
          fatigue: 'How your scores differed by the condition you reported.',
          range: 'The span of notes you can produce',
          registers: 'How evenly pitch holds across each register.',
          vibrato:
            'The regular oscillation of pitch — rate, depth, regularity.',
          breath: 'Wobble while holding a note.',
          formant: 'How well the resonance (ring) of the voice is formed.',
          resonance: 'Whether the ring sits bright or dark, spread or focused.',
        },
        // Three-step score badge (scoreLevel). Thresholds are uncalibrated.
        level: { high: 'Good', mid: 'Fair', low: 'Watch' },
        insightTitle: 'In one line',
        keyPoints: 'Key points',
        details: 'By item',
        legend: { mine: 'My result', average: 'Average' },
        insight: {
          registers: {
            lowExtension:
              'Your lowest stretch wavered most this time. Descend slowly and let the weight go.',
            modal:
              'Your speaking range wavered most this time. Start from a comfortable note and rebuild.',
            passaggio:
              'The handover from mid to high wavered most. That spot is hard for everyone.',
            head: 'Your high stretch wavered most this time. Widening what feels easy beats forcing higher notes.',
            highExtension:
              'Your highest stretch wavered most this time. That is the very edge, so grow it slowly.',
          },
          vibrato: {
            inBand: 'Your vibrato sits at a natural speed and width.',
            slow: 'Your vibrato runs slow, which can make the tone sound heavy.',
            fast: 'Your vibrato runs fast, which can make the tone sound rushed.',
            narrow:
              'Your vibrato is narrow — the pitch barely moves, so it hardly reads as vibrato.',
            wide: 'Your vibrato is wide — a big swing can blur which note you are on.',
          },
          breath: {
            start:
              'The wavering was largest at the start. Take a fuller breath before you begin.',
            middle: 'Your breath thinned in the middle and the wavering grew.',
            late: 'The wavering was largest at the end — that is where the air ran out.',
          },
          formant: {
            inBand: 'Your resonance sits inside the useful band.',
            centerLow:
              'Your resonance gathered below the useful band, which can sound thick.',
            centerHigh:
              'Your resonance gathered above the useful band, which can sound thin.',
            levelLow:
              'Your resonance is faint. The band is right, but little energy sits there.',
          },
          resonance: {
            concentrated:
              'Your resonance is tightly focused. Opening it up a little makes for a fuller, steadier sound.',
            wide: 'Your resonance is spread wide. Full, but it can lose focus.',
            dark: 'Your resonance leans dark — thick and soft.',
            bright:
              'Your resonance leans bright — clear, though it can sound thin.',
            balanced: 'Both brightness and spread sit near the middle.',
          },
        },
      },
      registers: {
        intro: 'Your range split into five stretches',
        legendStability: 'Steadiness (0-100)',
        legendHistory: 'vs your history (%)',
        desc: {
          lowExtension: 'fry-adjacent range',
          modal: 'main range',
          passaggio: 'peak effort',
          head: 'upper notes',
          highExtension: 'beyond useful range',
        },
        spanShort: '{{low}}-{{high}} · {{desc}}',
        section: 'Pitch — steadiness by register',
        // Donut label at the head of Figma §8.5.
        accuracy: 'Pitch accuracy',
        note: 'Your range split into five stretches, each scored on how evenly the voice came out. Stretches you did not sing this time are left blank.',
        span: '{{low}} - {{high}} · vs last {{history}}',
        history: '{{value}}%',
        historyNone: '—',
        name: {
          lowExtension: 'Low extension',
          modal: 'Modal',
          passaggio: 'Passaggio',
          head: 'Head',
          highExtension: 'High extension',
        },
      },
      breath: {
        legendScore: 'Breath steadiness score',
        legendAverage: 'Average baseline',
        bySegment: 'Steadiness by stretch',
        point: '{{value}} pts',
        desc: {
          high: 'Held steady.',
          mid: 'A little wobble.',
          low: 'The wobble grew.',
        },
        badge: { high: 'Steady', mid: 'Fair', low: 'Watch' },
        // Axis + alt text of the line chart (Figma §8.7).
        chartLabel: 'Breath steadiness by stretch',
        axisSec: '{{value}}s',
        axisScore: 'Vertical axis {{min}}–{{max}}',
        section: 'Breath steadiness',
        row: '{{name}} ({{from}}-{{to}}s)',
        name: { start: 'Start', middle: 'Middle', late: 'End' },
        note: 'Where the wavering sat inside one breath. Stretches the take never reached are left blank.',
      },
      formant: {
        tickHz: '{{value}}k',
        unitHz: 'Hz',
        unitDb: 'dB',
        section: "Singer's formant",
        note: 'Which band your resonance gathered in, and how strongly it sits there.',
        center: 'Resonance centre',
        centerValue: '{{value, number}} Hz',
        centerRange: 'Useful band {{min}}-{{max}} kHz',
        level: 'Resonance strength',
        levelValue: '{{value}} dB',
        levelRange: 'Useful band {{min}} to {{max}} dB',
      },
      resonance: {
        section: 'Resonance shape',
        note: 'Whether your resonance leans bright or dark, and whether it spreads or focuses.',
        brightness: 'Brightness',
        brightnessAxis: 'Dark ↔ Bright',
        spread: 'Spread',
        spreadAxis: 'Wide ↔ Focused',
        axisValue: '{{axis}} · {{value, number}} Hz',
        // 다섯 칸(TestFlight #59) — `stepOfPosition`.
        brightnessLevel: {
          veryLow: 'Darker',
          low: 'Slightly dark',
          mid: 'Balanced',
          high: 'Slightly bright',
          veryHigh: 'Brighter',
        },
        spreadLevel: {
          veryLow: 'Wider',
          low: 'Slightly wide',
          mid: 'Balanced',
          high: 'Slightly focused',
          veryHigh: 'More focused',
        },
        style: 'Resonance style',
        styleNote: 'Where your resonance sits right now.',
        here: 'Current position',
        axis: {
          top: 'Bright and clear',
          bottom: 'Dark and full',
          left: 'Wide ←',
          right: '→ Focused',
        },
      },
      vibrato: {
        axisSecShort: '{{value}}s',
        depthRef: 'Reference ±{{min}}-{{max}} cents',
        centsUnit: 'cents',
        rateRef: 'Ref {{min}}-{{max}} Hz',
        depthRatio: 'about {{ratio}}× a semitone',
        regularityBasis: 'from cycle variance',
        regularityScale: { low: 'Low', mid: 'Fair', high: 'Good' },
        explain: {
          title: 'Reading the depth',
          body: 'Vibrato depth (cents) is how far the pitch swings up and down.',
          small: { title: 'Shallow', desc: 'A small, plain shimmer' },
          good: { title: 'Just right', desc: 'Natural and full' },
          large: { title: 'Too deep', desc: 'A wide, unsteady wobble' },
        },
        // Waveform (Figma §8.6). **Not a picture of the recording** — it is drawn
        // from the two measured numbers.
        chartLabel: 'Vibrato sketch — {{rate}}Hz, {{depth}} cents wide',
        axisSec: '{{value}}s',
        axisCents: '±{{value}} cents',
        waveNote:
          'This is a sketch drawn from the measured rate and width, not the recorded waveform.',
        chartLabelMeasured:
          'Your vibrato this take — {{rate}}Hz, {{depth}} cents wide',
        waveNoteMeasured:
          'This is how your pitch actually wavered this take (3 s), with the slow drift taken out and the width set to the measured value.',
        section: 'Vibrato',
        rate: 'Rate',
        rateValue: '{{value}} Hz',
        rateNote: 'A natural wobble usually sits around {{min}}–{{max}} Hz.',
        depth: 'Depth',
        depthValue: '±{{value}} cents',
        depthNote: 'It swung {{ratio}}x of a semitone.',
        regularity: 'Regularity',
        regularityValue: '{{value}} %',
        regularityNote: 'How even the cycles of the wobble are.',
      },
      range: {
        // Figma §8.4 — top card and summary rows.
        wholeTitle: 'My full range',
        wholeDesc:
          'The lowest and highest notes your voice can produce right now',
        lowest: 'Lowest note',
        highest: 'Highest note',
        widest: 'Widest span',
        hz: '({{value}} Hz)',
        semitones: '({{value}} semitones)',
        section: 'Test 2. Low and high notes',
        // Legend of the keyboard bar (Figma §8.4).
        legendFilled: 'Notes you can reach',
        legendEmpty: 'Notes out of reach',
        mine: 'Your range',
        notes: '{{low}} ~ {{high}}',
        span: {
          whole: 'about {{octaves}} octaves',
          half: 'about {{octaves}}.5 octaves',
          halfOnly: 'about half an octave',
        },
        voiceType: 'Likely voice type',
        types: {
          bass: 'Bass-ish',
          baritone: 'Baritone-ish',
          tenor: 'Tenor-ish',
          contralto: 'Contralto-ish',
          mezzo: 'Mezzo-soprano-ish',
          soprano: 'Soprano-ish',
        },
        similar: {
          gwa: 'Similar to {{singers}} (estimate)',
          wa: 'Similar to {{singers}} (estimate)',
        },
        // Reference singers — romanised, same artists as the Korean list. Not
        // swapped for Western singers: who the reference is, is a content
        // decision that lives with the range table, not with the translation.
        singers: {
          choiBaekho: 'Choi Baek-ho',
          kimKwangseok: 'Kim Kwang-seok',
          kimDongryul: 'Kim Dong-ryul',
          sungSikyung: 'Sung Si-kyung',
          parkHyoshin: 'Park Hyo-shin',
          kimBumsoo: 'Kim Bum-soo',
          leeSora: 'Lee So-ra',
          jangJaein: 'Jang Jae-in',
          // 아이유 is already a Latin-script stage name — romanising the Hangul
          // ("A-i-yu") would be a name no English reader recognises.
          iu: 'IU',
          taeyeon: 'Taeyeon',
          parkJunghyun: 'Park Jung-hyun',
          sohyang: 'Sohyang',
        },
        register: 'Register shifts',
        registerTimes: '{{count}} time(s)',
        registerNone: 'No breaks on the way up',
        registerCount: 'Your voice broke {{count}} time(s) going up',
        registerPending: 'Did not climb high enough to judge',
      },
      // Result summary "Listen to my recordings" — take names reuse the diagnosis titles (`web.diagnosis.tests.<id>.title`).
      replay: {
        title: 'Listen to my recordings',
        play: 'Play {{take}}',
        stop: 'Stop {{take}}',
        failed: "Couldn't play the recording. Please try again in a moment.",
      },
      song: {
        section: 'Test 3. Sing along',
        accuracy: 'Sing-along pitch accuracy',
        percent: '{{value}}%',
        hits: 'About {{hit}} of {{total}} notes were on pitch',
        segments: 'By section',
        segment: {
          low: {
            onPitch: 'The lower section was accurate',
            flat: 'The lower section tended to sit slightly flat',
            sharp: 'The lower section tended to sit slightly sharp',
          },
          high: {
            onPitch: 'The higher section was accurate too',
            flat: 'The higher section tended to sit slightly flat',
            sharp: 'The higher section tended to sit slightly sharp',
          },
          pending: 'Not enough notes were sung to judge a section',
        },
        timing: 'Timing',
        timingSteady: 'You stayed with the beat',
        timingLate: 'You tended to come in slightly late',
        timingEarly: 'You tended to come in slightly early',
        timingPending: 'Not enough notes matched up to judge timing',
        skipped: 'You did not do the sing-along test this time',
      },
      summary: {
        section: 'Overall',
        total: 'Total',
        uncalibrated:
          'The total is a plain average of the measured axes (weights not calibrated)',
        axes: 'What the total is made of',
        type: 'Vocal type',
        type1: '{{p1}}',
        type2: '{{p1}}, {{p2}}',
        type3: '{{p1}}, {{p2}}, {{p3}}',
        typeParts: {
          tone: { low: 'Breathy', mid: 'Soft', high: 'Clear' },
          stability: { low: 'unsteady', mid: 'even', high: 'steady' },
          shape: {
            narrow: 'focused voice',
            balanced: 'balanced voice',
            wide: 'wide-range voice',
            unknown: 'voice',
          },
        },
        strengths: 'Strengths',
        improvements: 'To work on',
        points: {
          sustain: {
            strong: 'You hold a single breath for a long time',
            weak: 'A longer breath would make phrasing easier',
          },
          stability: {
            strong: 'Your tone stays even without wobbling',
            weak: 'Your pitch drifts a little while you hold a note',
          },
          clarity: {
            strong: 'Your tone is clean with little breath noise',
            weak: 'Breath noise blurs the tone at times',
          },
          resonance: {
            strong: 'Your tone has ring and carries well',
            weak: 'More resonance would carry your voice further',
          },
          range: {
            strong: 'You have a wide usable range',
            weak: 'Widening your range opens up more songs',
          },
          register: {
            strong: 'Your voice does not break going into high notes',
            weak: 'Your voice breaks where the register changes',
          },
          songAccuracy: {
            strong: 'You followed the reference melody closely',
            weak: 'Some passages drifted away from the melody',
          },
          timing: {
            strong: 'You came in on time',
            weak: 'Your note starts drift a little',
          },
        },
      },
      cta: 'Back to home',
    },
  },
};
